import React, { useState, useEffect } from 'react';
import axios from 'axios';
import RevealContact from './RevealContact';
import { Link } from 'react-router-dom';
import SendWhatsApp from './SendWhatsApp';

const STATUSES = ['lead', 'interested', 'walkin', 'negotiation', 'sold', 'lost'];
const SOURCES = ['broker', 'website', 'walkin', 'referral', 'meta', 'newspaper', 'hoarding', 'other'];
const DELETE_ROLES = ['admin', 'super_admin', 'superadmin', 'director', 'sales_head'];

const EMPTY = {
  name: '', phone: '', alternate_phone: '', email: '', address: '',
  source: 'broker', status: 'lead', project_id: '', channel_partner_id: '', assigned_to: '',
  budget_min: '', budget_max: '', bedrooms: '', notes: '', is_selling_property: false,
  send_welcome: false,
};

const asList = (d, key) => (Array.isArray(d) ? d : Array.isArray(d?.[key]) ? d[key] : Array.isArray(d?.data) ? d.data : []);

const Clients = ({ apiBase, user }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState(null);      // null = closed; {...} = open
  const [editId, setEditId] = useState(null);  // null = adding
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [notice, setNotice] = useState('');
  const [lookups, setLookups] = useState({ projects: [], partners: [], users: [] });
  const [dupes, setDupes] = useState([]);
  const [picked, setPicked] = useState([]);
  const [sendTo, setSendTo] = useState(null);

  const canDelete = DELETE_ROLES.includes(String(user?.role || '').toLowerCase());

  useEffect(() => {
    fetchClients();
    Promise.all([
      axios.get(`${apiBase}/projects`).catch(() => ({ data: [] })),
      axios.get(`${apiBase}/channel-partners`).catch(() => ({ data: [] })),
      axios.get(`${apiBase}/users`).catch(() => ({ data: [] })),
    ]).then(([p, cp, u]) => setLookups({
      projects: asList(p.data, 'projects'),
      partners: asList(cp.data, 'channel_partners'),
      users: asList(u.data, 'users'),
    }));
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${apiBase}/clients`);
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const flash = (msg) => { setNotice(msg); setTimeout(() => setNotice(''), 4000); };

  const openAdd = () => { setEditId(null); setForm({ ...EMPTY }); setFormError(''); setDupes([]); };

  const openEdit = async (id) => {
    setFormError(''); setDupes([]);
    try {
      const { data } = await axios.get(`${apiBase}/clients/${id}`);
      const f = { ...EMPTY };
      Object.keys(EMPTY).forEach((k) => { if (data[k] !== null && data[k] !== undefined) f[k] = data[k]; });
      f.is_selling_property = !!Number(data.is_selling_property);
      setEditId(id);
      setForm(f);
    } catch (e) {
      flash(e.response?.data?.error || 'Could not open this client.');
    }
  };

  const close = () => { if (!saving) { setForm(null); setEditId(null); } };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  const checkDuplicate = async () => {
    const digits = String(form?.phone || '').replace(/\D/g, '');
    if (editId || digits.length < 10) { setDupes([]); return; }
    try {
      const { data } = await axios.get(`${apiBase}/clients/check-duplicate/${digits.slice(-10)}`);
      setDupes(data?.matches || []);
    } catch (e) { setDupes([]); }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setFormError('Name is required.'); return; }
    if (!editId && String(form.phone).replace(/\D/g, '').length < 10) { setFormError('Enter a 10-digit phone number.'); return; }
    setSaving(true); setFormError('');
    const body = { ...form };
    ['project_id', 'channel_partner_id', 'assigned_to', 'budget_min', 'budget_max'].forEach((k) => { if (body[k] === '') body[k] = null; });
    try {
      if (editId) {
        delete body.send_welcome;
        await axios.put(`${apiBase}/clients/${editId}`, body);
        flash('Client updated.');
      } else {
        await axios.post(`${apiBase}/clients`, body);
        flash('Client added.');
      }
      setForm(null); setEditId(null);
      fetchClients();
    } catch (err) {
      setFormError(err.response?.status === 403 ? 'Your role is not allowed to do this.' : (err.response?.data?.error || 'Could not save.'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    const label = c.name && !String(c.name).includes('*') ? c.name : `client #${c.id}`;
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${apiBase}/clients/${c.id}`);
      setClients(clients.filter((x) => x.id !== c.id));
      flash('Client deleted.');
    } catch (err) {
      flash(err.response?.status === 403 ? 'Your role is not allowed to delete clients.' : (err.response?.data?.error || 'Could not delete.'));
    }
  };

  if (loading) {
    return <div className="loading">Loading clients...</div>;
  }

  const q = search.trim().toLowerCase();
  const shown = q
    ? clients.filter((c) => [c.name, c.phone, c.email, c.project_name, c.channel_partner_name, c.status]
      .some((v) => v && String(v).toLowerCase().includes(q)))
    : clients;

  const field = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' };
  const lbl = { display: 'block', fontSize: '12px', fontWeight: 600, color: '#4b5563', marginBottom: '4px' };
  const cell = { flex: '1 1 220px', minWidth: 0 };
  const cpLocked = editId && form && form.channel_partner_id;

  return (
    <div>
      <div className="card">
        <div className="card-header" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 className="card-title">Client Management (VDNB)</h2>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <input
              type="search" placeholder="Search name, phone, project…" value={search}
              onChange={(e) => setSearch(e.target.value)} style={{ ...field, width: '240px' }}
            />
            {picked.length > 0 && (
              <button className="btn btn-primary" style={{ background: '#25D366', borderColor: '#25D366' }}
                onClick={() => setSendTo(clients.filter((c) => picked.includes(c.id)).map((c) => ({ id: c.id, name: c.name })))}>
                💬 Send WhatsApp to {picked.length}
              </button>
            )}
            <button className="btn btn-primary" onClick={openAdd}>+ Add client</button>
          </div>
        </div>
        {notice && <div style={{ margin: '0 16px 12px', padding: '10px 14px', background: '#ecfdf5', color: '#065f46', borderRadius: '6px', fontSize: '14px' }}>{notice}</div>}
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: '32px' }}>
                  <input type="checkbox" title="Select all shown" checked={shown.length > 0 && shown.every((c) => picked.includes(c.id))}
                    onChange={(e) => setPicked(e.target.checked ? [...new Set([...picked, ...shown.map((c) => c.id)])] : picked.filter((id) => !shown.some((c) => c.id === id)))} />
                </th>
                <th>Name</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Source</th>
                <th>Channel Partner</th>
                <th>Status</th>
                <th>Project</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {shown.length === 0 && (
                <tr><td colSpan="9" style={{ textAlign: 'center', color: '#6b7280', padding: '24px' }}>
                  {clients.length ? 'No clients match your search.' : 'No clients yet — use "+ Add client".'}
                </td></tr>
              )}
              {shown.map((client) => (
                <tr key={client.id}>
                  <td><input type="checkbox" checked={picked.includes(client.id)} onChange={(e) => setPicked(e.target.checked ? [...picked, client.id] : picked.filter((id) => id !== client.id))} /></td>
                  <td>
                    <RevealContact row={client} field="name" entityType="client" apiBase={apiBase} />
                  </td>
                  <td>
                    <RevealContact row={client} field="phone" entityType="client" apiBase={apiBase} />
                  </td>
                  <td>{client.email || '-'}</td>
                  <td>{client.source || '-'}</td>
                  <td>{client.channel_partner_name || '-'}</td>
                  <td>
                    <span className={`badge badge-${getStatusColor(client.status)}`}>
                      {client.status}
                    </span>
                  </td>
                  <td>{client.project_name || '-'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <Link to={`/clients/${client.id}`} className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: '12px', marginRight: '4px' }}>
                      View Details
                    </Link>
                    <Link to={`/clients/${client.id}`} className="btn btn-primary" style={{ padding: '4px 8px', fontSize: '12px', marginRight: '4px' }}>
                      KYC & Booking
                    </Link>
                    <button className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: '12px', marginRight: '4px' }} onClick={() => openEdit(client.id)}>
                      Edit
                    </button>
                    {canDelete && (
                      <button className="btn btn-danger" style={{ padding: '4px 8px', fontSize: '12px' }} onClick={() => remove(client)}>
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {sendTo && <SendWhatsApp apiBase={apiBase} clients={sendTo} onClose={(sent) => { setSendTo(null); if (sent) setPicked([]); }} />}
      {form && (
        <div onClick={close} style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,.45)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '40px 16px' }}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} style={{ background: '#fff', borderRadius: '12px', width: '100%', maxWidth: '720px', padding: '20px 24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '18px' }}>{editId ? 'Edit client' : 'Add client'}</h3>
              <button type="button" onClick={close} style={{ border: 'none', background: 'none', fontSize: '22px', cursor: 'pointer', color: '#6b7280' }} aria-label="Close">×</button>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
              <div style={cell}><label style={lbl}>Name *</label><input style={field} value={form.name} onChange={set('name')} autoFocus /></div>
              <div style={cell}>
                <label style={lbl}>Phone {editId ? '' : '*'}</label>
                <input style={field} value={form.phone} onChange={set('phone')} onBlur={checkDuplicate} inputMode="tel" />
                {editId && String(form.phone).includes('*') && <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '3px' }}>Hidden until revealed — left unchanged unless you type a new number.</div>}
              </div>
              <div style={cell}><label style={lbl}>Alternate phone</label><input style={field} value={form.alternate_phone} onChange={set('alternate_phone')} inputMode="tel" /></div>
              <div style={cell}><label style={lbl}>Email</label><input style={field} type="email" value={form.email} onChange={set('email')} /></div>
              <div style={{ flex: '1 1 100%' }}><label style={lbl}>Address</label><input style={field} value={form.address} onChange={set('address')} /></div>

              <div style={cell}>
                <label style={lbl}>Status</label>
                <select style={field} value={form.status} onChange={set('status')}>
                  {[...new Set([...STATUSES, form.status])].filter(Boolean).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div style={cell}>
                <label style={lbl}>Source</label>
                <select style={field} value={form.source} onChange={set('source')}>
                  {[...new Set([...SOURCES, form.source])].filter(Boolean).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div style={cell}>
                <label style={lbl}>Project</label>
                <select style={field} value={form.project_id || ''} onChange={set('project_id')}>
                  <option value="">—</option>
                  {lookups.projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div style={cell}>
                <label style={lbl}>Channel partner</label>
                <select style={field} value={form.channel_partner_id || ''} onChange={set('channel_partner_id')} disabled={!!cpLocked}>
                  <option value="">—</option>
                  {lookups.partners.map((p) => <option key={p.id} value={p.id}>{p.name}{p.company_name ? ` (${p.company_name})` : ''}</option>)}
                </select>
                {cpLocked && <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '3px' }}>Once tagged, the channel partner cannot be changed.</div>}
              </div>
              {lookups.users.length > 0 && (
                <div style={cell}>
                  <label style={lbl}>Assigned to</label>
                  <select style={field} value={form.assigned_to || ''} onChange={set('assigned_to')}>
                    <option value="">—</option>
                    {lookups.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
              )}
              <div style={cell}><label style={lbl}>Configuration</label><input style={field} value={form.bedrooms || ''} onChange={set('bedrooms')} placeholder="e.g. 2 BHK" /></div>
              <div style={cell}><label style={lbl}>Budget from (₹)</label><input style={field} type="number" min="0" value={form.budget_min || ''} onChange={set('budget_min')} /></div>
              <div style={cell}><label style={lbl}>Budget to (₹)</label><input style={field} type="number" min="0" value={form.budget_max || ''} onChange={set('budget_max')} /></div>
              <div style={{ flex: '1 1 100%' }}><label style={lbl}>Notes</label><textarea style={{ ...field, minHeight: '70px' }} value={form.notes || ''} onChange={set('notes')} /></div>
              <label style={{ flex: '1 1 100%', fontSize: '14px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input type="checkbox" checked={!!form.is_selling_property} onChange={set('is_selling_property')} /> Client is also selling a property
              </label>
              {!editId && (
                <label style={{ flex: '1 1 100%', fontSize: '14px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input type="checkbox" checked={!!form.send_welcome} onChange={set('send_welcome')} /> Send the welcome message to the client now
                </label>
              )}
            </div>

            {dupes.length > 0 && (
              <div style={{ marginTop: '12px', padding: '10px 14px', background: '#fffbeb', color: '#92400e', borderRadius: '6px', fontSize: '13px' }}>
                This number already exists: {dupes.map((d) => `${d.name || '—'} (${d.source})`).join(', ')}.
              </div>
            )}
            {formError && <div style={{ marginTop: '12px', padding: '10px 14px', background: '#fef2f2', color: '#b91c1c', borderRadius: '6px', fontSize: '13px' }}>{formError}</div>}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '18px' }}>
              <button type="button" className="btn btn-secondary" onClick={close} disabled={saving}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : editId ? 'Save changes' : 'Add client'}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

const getStatusColor = (status) => {
  const colors = {
    'lead': 'info',
    'interested': 'warning',
    'sold': 'success',
    'lost': 'danger'
  };
  return colors[status] || 'info';
};

export default Clients;
